import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, errMsg, REGIMI, MODALITA } from "@/lib/api";
import { PageHeader, Field, Panel, QuotaBar } from "@/components/Common";
import { LogoUploader } from "@/components/LogoUploader";
import { PdfTemplatePanel } from "@/components/PdfTemplatePanel";
import { useAuth } from "@/context/AuthContext";
import { SdiPanel } from "@/components/SdiPanel";
import { CassaFields } from "@/components/InvoiceExtras";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const ANAG = [["denominazione", "Ragione sociale", "sm:col-span-2"], ["piva", "Partita IVA"], ["codice_fiscale", "Codice fiscale"],
  ["indirizzo", "Indirizzo sede", "sm:col-span-2"], ["cap", "CAP"], ["comune", "Comune"], ["provincia", "Provincia"], ["nazione", "Nazione"],
  ["email", "Email"], ["pec", "PEC"], ["telefono", "Telefono"], ["codice_destinatario", "Tuo codice destinatario SdI (per autofatture)"]];
const REA = [["rea_ufficio", "Provincia ufficio REA"], ["rea_numero", "Numero iscrizione REA"], ["capitale_sociale", "Capitale sociale (€)"], ["eori", "Codice EORI"],
  ["albo_nome", "Categoria / albo professionale (es. Ordine dei Dottori Commercialisti)"], ["albo_provincia", "Provincia albo (sigla)"], ["albo_numero", "Numero iscrizione albo"], ["albo_data", "Data iscrizione albo", "date"]];
const Pick = ({ label, value, onChange, opts, testid }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(opts).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);
const ReaPanel = ({ c, set }) => (
  <Panel title="Iscrizione REA e albo professionale" className="lg:col-span-2">
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Pick label="Socio unico" testid="settings-socio-select" value={c.socio_unico || "none"} onChange={(v) => set("socio_unico", v === "none" ? "" : v)} opts={{ none: "Non indicato", SU: "Socio unico", SM: "Più soci" }} />
      <Pick label="Stato liquidazione" testid="settings-liquidazione-select" value={c.stato_liquidazione || "LN"} onChange={(v) => set("stato_liquidazione", v)} opts={{ LN: "Non in liquidazione", LS: "In liquidazione" }} />
      {REA.map(([k, l, type]) => <Field key={k} label={l}><Input type={type || "text"} data-testid={`settings-${k}-input`} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>)}
      <p className="text-xs text-slate-500 sm:col-span-2 lg:col-span-4">I dati REA vanno in fattura solo se compili provincia e numero; quelli dell'albo solo se indichi il nome dell'albo.</p>
    </div>
  </Panel>
);

export default function Settings() {
  const [c, setC] = useState(null);
  const { user, reload } = useAuth();
  useEffect(() => { api.get("/company").then((r) => setC(r.data)); }, []);
  if (!c) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const set = (k, v) => setC({ ...c, [k]: v });

  const save = async () => {
    try {
      const { data } = await api.put("/company", { ...c, giorni_scadenza: Number(c.giorni_scadenza || 0), codice_destinatario: c.codice_destinatario || "0000000", prefix_af: c.prefix_af ?? "AF", pdf_template: c.pdf_template || "griglia", pdf_color: c.pdf_color || "#0F172A" });
      setC({ ...c, ...data, sezionali_txt: undefined });
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Impostazioni azienda" subtitle="Dati del cedente riportati in ogni fattura XML">
        <Button data-testid="save-settings-button" className="bg-sky-600 hover:bg-sky-700" onClick={save}><Save className="mr-2 h-4 w-4" />Salva</Button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Dati fiscali" className="lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {ANAG.map(([k, l, cls]) => (
              <Field key={k} label={l} className={cls}><Input data-testid={`settings-${k}-input`} value={c[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>
            ))}
            <Field label="Regime fiscale" className="sm:col-span-2">
              <Select value={c.regime_fiscale || "RF01"} onValueChange={(v) => set("regime_fiscale", v)}>
                <SelectTrigger data-testid="settings-regime-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(REGIMI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </Panel>
        <ReaPanel c={c} set={set} />
        <div className="space-y-6 lg:col-start-3 lg:row-start-1 lg:row-span-2">
          {!user.demo && <Panel title="Logo aziendale">
            <LogoUploader endpoint="/company/logo" value={c.logo} testid="company-logo" hint="Appare nel menu e sul PDF delle fatture. PNG o JPG, max 280 KB" onSaved={(logo) => { setC((x) => ({ ...x, logo })); reload(); }} />
          </Panel>}
          <Panel title="Pacchetto e studio">
            <div className="space-y-3 text-sm">
              <div><span className="text-slate-500">Pacchetto:</span> <b data-testid="settings-package-name">{c.package_name || "Nessuno"}</b></div>
              <div><span className="text-slate-500">Studio abbinato:</span> <b>{c.studio_name || "Nessuno"}</b></div>
              <QuotaBar used={c.xml_used} quota={c.xml_quota} />
            </div>
          </Panel>
          <Panel title="Intermediario SdI">
            <div data-testid="sdi-mode" className={`rounded-md px-3 py-2 text-sm ${c.sdi_mock ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
              {c.sdi_mock ? "SIMULAZIONE: Openapi non ancora attivo per questa azienda. Gli invii non raggiungono lo SdI." : c.sdi_ambiente === "demo" ? "Collegato a Openapi in PROVA (sandbox): gli invii non arrivano all'Agenzia delle Entrate." : "Collegato a Openapi in PRODUZIONE: gli invii raggiungono lo SdI."}
            </div>
          </Panel>
        </div>
        <Panel title="Numerazione" className="lg:col-span-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Prefisso fatture"><Input data-testid="settings-prefix-fe-input" placeholder="es. FE" value={c.prefix_fe || ""} onChange={(e) => set("prefix_fe", e.target.value)} /></Field>
            <Field label="Prefisso note credito"><Input data-testid="settings-prefix-nc-input" value={c.prefix_nc || ""} onChange={(e) => set("prefix_nc", e.target.value)} /></Field>
            <Field label="Prefisso autofatture"><Input data-testid="settings-prefix-af-input" value={c.prefix_af ?? "AF"} onChange={(e) => set("prefix_af", e.target.value)} /></Field>
            <Field label="Altri sezionali fatture (separati da virgola)" className="sm:col-span-2">
              <Input data-testid="settings-sezionali-input" placeholder="es. FPR, FPA" value={c.sezionali_txt ?? (c.sezionali || []).join(", ")}
                onChange={(e) => setC((x) => ({ ...x, sezionali_txt: e.target.value, sezionali: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) }))} />
            </Field>
            <p className="text-xs text-slate-500 sm:col-span-2">Ogni sezionale ha la sua numerazione (es. FPR 1/{new Date().getFullYear()}) e si sceglie nella maschera della fattura.</p>
          </div>
          <p className="mt-3 font-mono text-xs text-slate-500">Esempio: {c.prefix_fe}1/{new Date().getFullYear()} · {c.prefix_nc}1/{new Date().getFullYear()}</p>
        </Panel>
        <Panel title="Pagamento predefinito" className="lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Modalità">
              <Select value={c.modalita_pagamento || "MP05"} onValueChange={(v) => set("modalita_pagamento", v)}>
                <SelectTrigger data-testid="settings-modalita-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MODALITA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Giorni scadenza"><Input data-testid="settings-giorni-input" type="number" value={c.giorni_scadenza ?? 30} onChange={(e) => set("giorni_scadenza", e.target.value)} /></Field>
            <Field label="IBAN"><Input data-testid="settings-iban-input" className="font-mono" value={c.iban || ""} onChange={(e) => set("iban", e.target.value)} /></Field>
          </div>
        </Panel>
        <Panel title="Cassa previdenziale di appartenenza" className="lg:col-span-3">
          <p className="mb-3 text-sm text-slate-500">Viene inserita in automatico in ogni nuova fattura (resta modificabile). Le righe con natura N1 (spese anticipate art. 15) sono escluse dal calcolo.</p>
          <div className="max-w-3xl" data-testid="settings-cassa">
            <CassaFields c={{ tipo: "", aliquota: 4, aliquota_iva: c.regime_fiscale === "RF19" ? 0 : 22, natura: c.regime_fiscale === "RF19" ? "N2.2" : "", ritenuta: false, ...(c.cassa_default || {}) }}
              setC={(v) => set("cassa_default", { ...v, aliquota: Number(v.aliquota || 0), aliquota_iva: Number(v.aliquota_iva || 0) })} forfettario={c.regime_fiscale === "RF19"} />
          </div>
        </Panel>
        <Panel title="ENASARCO · agenti di commercio" className="lg:col-span-3">
          <p className="mb-3 text-sm text-slate-500">Massimale annuo delle provvigioni soggette a contributo per ogni preponente (cliente). Superato il massimale, il contributo ENASARCO in fattura si azzera fino a fine anno. Lascia 0 se non sei agente.</p>
          <Field label="Massimale provvigionale annuo per preponente (€)"><Input data-testid="settings-enasarco-massimale" className="max-w-xs" type="number" step="0.01" min="0" value={c.enasarco_massimale ?? 0} onChange={(e) => set("enasarco_massimale", Number(e.target.value || 0))} /></Field>
        </Panel>
        <Panel title="Accesso Avatar dello Studio Principale" className="lg:col-span-3">
          <label className="flex items-center gap-3 text-sm">
            <Switch data-testid="settings-avatar-consenso" disabled={user.acting_superadmin} checked={!!c.avatar_consenso} onCheckedChange={(v) => set("avatar_consenso", v)} />
            Consenti allo Studio Principale di entrare nella mia azienda (modalità Avatar) per assistenza
          </label>
          <p className="mt-2 text-xs text-slate-500">Puoi spegnerlo quando vuoi. Ogni accesso dura al massimo 2 ore e viene registrato. Salva le impostazioni per applicare.</p>
        </Panel>
        <PdfTemplatePanel c={c} set={set} className="lg:col-span-3" />
        {!user.demo && <SdiPanel className="lg:col-span-3" />}
      </div>
    </div>
  );
}
